import { Button, ButtonType, CheckboxInput, SkeletonLoader, sizing } from '@chillibean/component-library';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { FilterPicker } from '../../../../components/presentational';
import { DisplayNameType, useDisplayName, useStudyEventDefinitionsFilteredByStudyArm } from '../../../../hooks';
import { setStudyEventDefinitionFilteredByStudyArm } from '../../../../redux/actions';
import { DEFAULT_FILTERED_DATA } from '../../../../redux/reducers/protocolReducer';

export function StudySchedulerHeaderLoading() {
  return (
    <Header>
      <SkeletonLoader height={38} preserveAspectRatio="none" width={128} />
      <SkeletonLoader height={38} preserveAspectRatio="none" width={400} />
    </Header>
  );
}

export function StudySchedulerHeader(props) {
  const { loading = false, openStudyEventDefinitionModal, openStudyProcedureDefinitionModal, selectAllParams } = props;
  const { disabled, isAllSelected, select, unselect } = selectAllParams;
  const { handleSelectAll, selectAllLoading } = select;
  const { handleUnselectAll, unselectAllLoading } = unselect;

  // VARIABLES
  const isLoading = () => (isAllSelected ? unselectAllLoading : selectAllLoading);
  const handleOnChange = () => (isAllSelected ? handleUnselectAll() : handleSelectAll());
  // HOOKS
  // Store
  const dispatch = useDispatch();
  const { data: protocol } = useSelector((state) => state.protocolReducer.protocol);
  const { studyArms } = protocol || {};
  // Custom
  const { getDisplayName } = useDisplayName();
  const { studyEventDefinitionFilteredByStudyArm } = useStudyEventDefinitionsFilteredByStudyArm();

  const handleClickFilterOption = (option) => {
    dispatch(setStudyEventDefinitionFilteredByStudyArm({ data: option }));
  };

  if (loading) return <StudySchedulerHeaderLoading />;

  return (
    <Header>
      <CheckboxInput
        checked={isAllSelected}
        disabled={protocol?.locked || isLoading() || disabled}
        label="Select all"
        onChange={handleOnChange}
        value={1}
      />
      <Actions>
        <FilterPicker
          currentOption={studyEventDefinitionFilteredByStudyArm}
          onClickOption={handleClickFilterOption}
          options={[...DEFAULT_FILTERED_DATA, ...studyArms.map((studyArm) => studyArm.shortStudyArm())]}
        />
        <Button
          disabled={protocol?.locked}
          loading={false}
          onClick={openStudyEventDefinitionModal}
          text={`Add ${getDisplayName(DisplayNameType.STUDY_EVENT).humanized.singular}`}
          type={ButtonType.PRIMARY}
        />
        <Button
          disabled={protocol?.locked}
          loading={false}
          onClick={openStudyProcedureDefinitionModal}
          text={`Add ${getDisplayName(DisplayNameType.STUDY_PROCEDURE).humanized.singular}`}
          type={ButtonType.PRIMARY}
        />
      </Actions>
    </Header>
  );
}

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizing.medium};
`;

StudySchedulerHeader.propTypes = {
  loading: PropTypes.bool,
  openStudyEventDefinitionModal: PropTypes.func.isRequired,
  openStudyProcedureDefinitionModal: PropTypes.func.isRequired,
  selectAllParams: PropTypes.shape({
    disabled: PropTypes.bool,
    isAllSelected: PropTypes.bool,
    select: PropTypes.shape({
      handleSelectAll: PropTypes.func,
      selectAllLoading: PropTypes.bool,
    }),
    unselect: PropTypes.shape({
      handleUnselectAll: PropTypes.func,
      unselectAllLoading: PropTypes.bool,
    }),
  }).isRequired,
};

export default StudySchedulerHeader;
